import { PayloadAction } from "@reduxjs/toolkit";
import { all, call, put, select, takeLatest } from "redux-saga/effects";
import {
  CategoryCount,
  DemoTestTotals,
  DistributorComparison,
  DistributorSalesData,
  DistributorWithCountry,
  MultiAccountDevices,
  OrgBreakdown,
  OrgTestCountAnalytics,
  TestCountAnalytics,
} from "types/analyticsTypes";
import { selectUserDistributorId } from "@utils/redux-utils/selectors/userSelectors";
import {
  fetchAllDistributorIds,
  fetchCategoryDistribution,
  fetchDemoTestTotals,
  fetchDeviceTestCounts,
  fetchDistributorComparison,
  fetchDistributorSales,
  fetchDistributorTestCounts,
  fetchMultiAccountDevices,
  fetchOrgBreakdown,
  fetchOrgTestCounts,
} from "@utils/api-communication/RestApiCli";
import {
  requestAllDistributorIds,
  requestCategoryDistribution,
  requestDemoTestTotals,
  requestDistributorComparison,
  requestDistributorOrgTestCounts,
  requestDistributorSales,
  requestMultiAccountDevices,
  requestOrgBreakdown,
  requestOrgTestCounts,
  requestSingleDeviceTestCounts,
  setAllDistributorIds,
  setCategoryDistribution,
  setDemoTestTotals,
  setDistributorComparison,
  setDistributorOrgTestCounts,
  setDistributorSales,
  setMultiAccountDevices,
  setOrgBreakdown,
  setOrgTestCounts,
  setSingleDeviceTestCounts,
} from "./analyticsSlice";

interface DateRangeParams {
  startDate?: string;
  endDate?: string;
}

function* handleRequestDistributorSales(
  action: PayloadAction<{ distributorId?: string } & DateRangeParams>,
) {
  const distId = action.payload.distributorId ?? (yield select(selectUserDistributorId));
  yield put(setDistributorSales(null));
  try {
    const data: DistributorSalesData = yield call(
      fetchDistributorSales,
      distId,
      action.payload.startDate,
      action.payload.endDate,
    );
    yield put(setDistributorSales(data));
  } catch (error) {
    yield put(setDistributorSales(null));
  }
}

function* handleRequestDistributorOrgTestCounts(
  action: PayloadAction<{ distributorId?: string } & DateRangeParams>,
) {
  const distId = action.payload.distributorId ?? (yield select(selectUserDistributorId));
  yield put(setDistributorOrgTestCounts(null));
  try {
    const data: OrgTestCountAnalytics[] = yield call(
      fetchDistributorTestCounts,
      distId,
      action.payload.startDate,
      action.payload.endDate,
    );
    yield put(setDistributorOrgTestCounts(data));
  } catch (error) {
    yield put(setDistributorOrgTestCounts(null));
  }
}

function* handleRequestCategoryDistribution(
  action: PayloadAction<{ distributorId?: string } & DateRangeParams>,
) {
  const distId = action.payload.distributorId ?? (yield select(selectUserDistributorId));
  yield put(setCategoryDistribution(null));
  try {
    const data: CategoryCount[] = yield call(
      fetchCategoryDistribution,
      distId,
      action.payload.startDate,
      action.payload.endDate,
    );
    yield put(setCategoryDistribution(data));
  } catch (error) {
    yield put(setCategoryDistribution(null));
  }
}

function* handleRequestDemoTestTotals(action: PayloadAction<{ distributorId?: string }>) {
  const distId = action.payload.distributorId ?? (yield select(selectUserDistributorId));
  yield put(setDemoTestTotals(null));
  try {
    const data: DemoTestTotals = yield call(fetchDemoTestTotals, distId);
    yield put(setDemoTestTotals(data));
  } catch (error) {
    yield put(setDemoTestTotals(null));
  }
}

function* handleRequestDistributorComparison(action: PayloadAction<DateRangeParams>) {
  yield put(setDistributorComparison(null));
  try {
    const data: DistributorComparison = yield call(
      fetchDistributorComparison,
      action.payload.startDate,
      action.payload.endDate,
    );
    yield put(setDistributorComparison(data));
  } catch (error) {
    yield put(setDistributorComparison(null));
  }
}

function* handleRequestMultiAccountDevices(action: PayloadAction<{ distributorId?: string }>) {
  const distId = action.payload.distributorId ?? (yield select(selectUserDistributorId));
  yield put(setMultiAccountDevices(null));
  try {
    const data: MultiAccountDevices = yield call(fetchMultiAccountDevices, distId);
    yield put(setMultiAccountDevices(data));
  } catch (error) {
    yield put(setMultiAccountDevices(null));
  }
}

function* handleRequestOrgBreakdown(action: PayloadAction<{ orgId: string } & DateRangeParams>) {
  try {
    const breakdown: OrgBreakdown = yield call(
      fetchOrgBreakdown,
      action.payload.orgId,
      action.payload.startDate,
      action.payload.endDate,
    );
    yield put(setOrgBreakdown({ orgId: action.payload.orgId, breakdown }));
  } catch (error) {
    console.error("Error fetching org breakdown:", error);
    yield put(
      setOrgBreakdown({
        orgId: action.payload.orgId,
        breakdown: { devices: [], categories: [] },
      }),
    );
  }
}

function* handleRequestOrgTestCounts(action: PayloadAction<{ orgId: string }>) {
  const orgId = action.payload.orgId;
  yield put(setOrgTestCounts(null));
  try {
    const data: OrgTestCountAnalytics = yield call(fetchOrgTestCounts, orgId);
    yield put(setOrgTestCounts(data));
  } catch (error) {
    yield put(setOrgTestCounts(null));
  }
}

function* handleRequestSingleDeviceTestCounts(
  action: PayloadAction<{ deviceId: string; orgId: string }>,
) {
  yield put(setSingleDeviceTestCounts(null));
  try {
    const data: TestCountAnalytics = yield call(
      fetchDeviceTestCounts,
      action.payload.deviceId,
      action.payload.orgId,
    );
    yield put(setSingleDeviceTestCounts(data));
  } catch (error) {
    console.error("Error fetching single device test counts:", error);
    yield put(setSingleDeviceTestCounts(null));
  }
}

function* handleRequestAllDistributorIds() {
  try {
    const data: DistributorWithCountry[] = yield call(fetchAllDistributorIds);
    yield put(setAllDistributorIds(data));
  } catch (error) {
    console.error("Error fetching all distributor IDs:", error);
    yield put(setAllDistributorIds(null));
  }
}

export default function* analyticsSagas() {
  yield all([
    takeLatest(requestDistributorSales.type, handleRequestDistributorSales),
    takeLatest(requestDistributorOrgTestCounts.type, handleRequestDistributorOrgTestCounts),
    takeLatest(requestCategoryDistribution.type, handleRequestCategoryDistribution),
    takeLatest(requestDemoTestTotals.type, handleRequestDemoTestTotals),
    takeLatest(requestDistributorComparison.type, handleRequestDistributorComparison),
    takeLatest(requestMultiAccountDevices.type, handleRequestMultiAccountDevices),
    takeLatest(requestOrgBreakdown.type, handleRequestOrgBreakdown),
    takeLatest(requestOrgTestCounts.type, handleRequestOrgTestCounts),
    takeLatest(requestSingleDeviceTestCounts.type, handleRequestSingleDeviceTestCounts),
    takeLatest(requestAllDistributorIds.type, handleRequestAllDistributorIds),
  ]);
}
